import { FC, useCallback, useEffect, useMemo, useState } from 'react';
import { Button, Empty, Table, Typography } from 'antd';
import { ColumnProps } from 'antd/lib/table';
import { PlusOutlined } from '@ant-design/icons';
import { Link, useNavigate } from 'react-router-dom';
import { fetchInvoices } from 'services/api/invoicing';
import { Invoice } from 'types/api/invoicing';

import { documentCell, INVOICE_COLUMNS } from 'pages/Invoicing/invoiceColumns';
import { showErrorMessage } from 'utils/showErrorMessage';
import { IPatientPageProps } from '../../_types';

const PAGE_LIMIT = 10;

/**
 * The patient's full invoice history — `GET /invoicing?patientId=`. Unlike the
 * general invoices table this includes drafts and cancelled ones. Invoices
 * billed to a non-user customer carry no patientId and never appear here (BE
 * excludes them by design); those live only in the general table.
 */
const Invoices: FC<IPatientPageProps> = ({ patient }) => {
  const navigate = useNavigate();

  const [invoices, setInvoices] = useState<Invoice[]>([]);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(1);
  const [loading, setLoading] = useState(true);

  const patientId = patient?.id;

  useEffect(() => {
    if (!patientId) {
      return undefined;
    }

    let cancelled = false;

    const load = async () => {
      try {
        setLoading(true);
        const res = await fetchInvoices({
          patientId,
          page,
          limit: PAGE_LIMIT
        });

        if (cancelled) return;
        setInvoices(res.data);
        setTotal(res.total);
      } catch (err) {
        if (!cancelled) showErrorMessage(err);
      } finally {
        if (!cancelled) setLoading(false);
      }
    };

    load();

    return () => {
      cancelled = true;
    };
  }, [patientId, page]);

  const onCreate = useCallback(
    () => navigate('/invoicing/invoices/create'),
    [navigate]
  );

  const columns: ColumnProps<Invoice>[] = useMemo(
    () => [
      {
        ...INVOICE_COLUMNS.invoiceNumber,
        // Keep the shared credit-note tag while making the number a link.
        render: (value: string | null, row: Invoice) =>
          documentCell(
            row,
            <Link to={`/invoicing/invoices/${row.publicId}`}>
              {value || 'Draft'}
            </Link>
          )
      },
      INVOICE_COLUMNS.status,
      INVOICE_COLUMNS.paymentMethod,
      INVOICE_COLUMNS.total,
      INVOICE_COLUMNS.due,
      INVOICE_COLUMNS.created
    ],
    []
  );

  return (
    <>
      <Table
        dataSource={invoices}
        columns={columns}
        loading={loading}
        rowKey="publicId"
        scroll={{ x: 'max-content' }}
        locale={{
          emptyText: (
            <Empty
              image={Empty.PRESENTED_IMAGE_SIMPLE}
              description="No invoices for this patient yet"
            >
              <Button type="primary" icon={<PlusOutlined />} onClick={onCreate}>
                New invoice
              </Button>
            </Empty>
          )
        }}
        pagination={{
          total,
          current: page,
          pageSize: PAGE_LIMIT,
          onChange: setPage,
          showSizeChanger: false,
          hideOnSinglePage: true
        }}
      />
      {!loading && total > 0 && (
        <Typography.Paragraph type="secondary" style={{ marginTop: 16 }}>
          Invoices billed to a non-user customer are not listed here — find them
          in <Link to="/invoicing/invoices">Invoicing</Link>.
        </Typography.Paragraph>
      )}
    </>
  );
};

export default Invoices;
